import { Select, SelectOption, Switch } from '@acast-tech/decibel';
import type { ChartGranularity } from '@/types';
import { useFilterContext } from '@/context';
import usePageMetadata from '@/hooks/usePageMetadata';
import { MIXPANEL_EVENT } from '@/utils/mixpanelUtils/events';
import { trackContentPerformanceEventIfApplicable } from '@/routes/ContentPerformancePage/trackingUtils';

interface ChartControlsProps {
  granularity: ChartGranularity;
  onGranularityChange: (granularity: ChartGranularity) => void;
  displayPublishDates: boolean;
  onDisplayPublishDatesChange: (show: boolean) => void;
  /** When set with `onDisplayTotalChange`, shows the Display total toggle (legacy chart only). */
  displayTotal?: boolean;
  onDisplayTotalChange?: (show: boolean) => void;
}

/**
 * Chart controls component
 * @param granularity - The granularity
 * @param onGranularityChange - The function to call when the granularity changes
 * @param displayPublishDates - Whether to display publish dates
 * @param onDisplayPublishDatesChange - The function to call when the show publish dates changes
 * @param displayTotal - Whether to display the total for all episodes in the selected show or network
 * @param onDisplayTotalChange - The function to call when the display total changes
 * @returns The chart controls
 */
const ChartControls = ({
  granularity,
  onGranularityChange,
  displayPublishDates,
  onDisplayPublishDatesChange,
  displayTotal,
  onDisplayTotalChange,
}: ChartControlsProps) => {
  const { page } = usePageMetadata();
  const { filters } = useFilterContext();
  const showTotalToggle = displayTotal !== undefined && onDisplayTotalChange !== undefined;
  const totalToggleLabel =
    filters.selectedShows.length > 0 ? 'Display show total' : 'Display network total';

  return (
    <div className="flex w-full md:justify-end my-6">
      <div className="flex w-full flex-col items-stretch gap-5 md:w-auto md:flex-row md:items-center md:gap-10">
        <Switch
          id="toggle-publish-dates"
          label="Display publish dates"
          checked={displayPublishDates}
          onChange={() => {
            const next = !displayPublishDates;
            trackContentPerformanceEventIfApplicable(
              page,
              MIXPANEL_EVENT.contentPerformance.chart.publishDatesToggled,
              { toggle: 'publishDates', enabled: next }
            );
            onDisplayPublishDatesChange(next);
          }}
        />
        {showTotalToggle && (
          <Switch
            id="toggle-display-total"
            label={totalToggleLabel}
            checked={displayTotal}
            onChange={() => {
              const next = !displayTotal;
              trackContentPerformanceEventIfApplicable(
                page,
                MIXPANEL_EVENT.contentPerformance.chart.displayTotalToggled,
                { toggle: 'displayTotal', enabled: next }
              );
              onDisplayTotalChange(next);
            }}
          />
        )}
        <Select
          style={{ fontSize: '14px', cursor: 'pointer' }}
          value={granularity}
          stretch={false}
          onChange={(e) => {
            const next = (e.target as HTMLSelectElement).value as ChartGranularity;
            trackContentPerformanceEventIfApplicable(
              page,
              MIXPANEL_EVENT.contentPerformance.chart.granularityChanged,
              { granularity: next }
            );
            onGranularityChange(next);
          }}
        >
          <SelectOption value="day">Daily</SelectOption>
          <SelectOption value="week">Weekly</SelectOption>
          <SelectOption value="month">Monthly</SelectOption>
        </Select>
      </div>
    </div>
  );
};

export default ChartControls;
